/**
 * Checkout Page
 *
 * Handles the checkout process with customer info, shipping, and payment.
 * Accessible at /s/:storeSlug/checkout
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';

interface Store {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  theme: StoreTheme;
  currency: string;
  locale: LanguageCode;
  contact_email?: string;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
}

interface CartItem {
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  price: number;
  quantity: number;
  image?: string;
}

interface CustomerInfo {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface ShippingAddress {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
};

/**
 * Safely parse JSON from localStorage with fallback
 */
function safeParseJSON<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    console.warn('Failed to parse JSON from localStorage, using fallback');
    return fallback;
  }
}

export default function CheckoutPage() {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('store');
  const [store, setStore] = useState<Store | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'info' | 'shipping' | 'review'>('info');

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
  });

  useEffect(() => {
    async function loadStore() {
      try {
        setLoading(true);

        // Load store data
        const response = await fetch(`/api/public/stores/${storeSlug}`);
        if (!response.ok) {
          setError('Store not found');
          return;
        }

        const data = await response.json() as { store: Store };
        setStore(data.store);

        // Load cart from localStorage
        const cartKey = `cart_${data.store.id}`;
        const cart = safeParseJSON<CartItem[]>(localStorage.getItem(cartKey), []);

        if (cart.length === 0) {
          // Redirect to cart if empty
          navigate(`/s/${storeSlug}/cart`);
          return;
        }

        setCartItems(cart);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load store');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug) {
      loadStore();
    }
  }, [storeSlug, navigate]);

  const theme = store?.theme || defaultTheme;
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const storeLocale = store?.locale || 'en';
  const storeDirection = getLanguageDirection(storeLocale);

  // Change language when store locale is set
  useEffect(() => {
    if (store?.locale && store.locale !== i18n.language) {
      i18n.changeLanguage(store.locale);
    }
  }, [store?.locale, i18n]);

  const formatPrice = (price: number, currency: string) => {
    const localeMap: Record<string, string> = {
      en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR',
      ar: 'ar-SA', bn: 'bn-BD', pt: 'pt-BR', ru: 'ru-RU', ja: 'ja-JP',
      de: 'de-DE', ko: 'ko-KR', vi: 'vi-VN', tr: 'tr-TR', it: 'it-IT',
      th: 'th-TH', pl: 'pl-PL', nl: 'nl-NL', id: 'id-ID', he: 'he-IL',
    };
    const intlLocale = localeMap[storeLocale] || 'en-US';
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price / 100);
  };

  const validateCustomerInfo = () => {
    if (!customerInfo.email || !customerInfo.email.includes('@')) {
      setError(t('validation.emailInvalid'));
      return false;
    }
    if (!customerInfo.firstName.trim()) {
      setError(t('validation.firstNameRequired'));
      return false;
    }
    if (!customerInfo.lastName.trim()) {
      setError(t('validation.lastNameRequired'));
      return false;
    }
    setError(null);
    return true;
  };

  const validateShippingAddress = () => {
    if (!shippingAddress.address1.trim()) {
      setError(t('validation.addressRequired'));
      return false;
    }
    if (!shippingAddress.city.trim()) {
      setError(t('validation.cityRequired'));
      return false;
    }
    if (!shippingAddress.postalCode.trim()) {
      setError(t('validation.postalCodeRequired'));
      return false;
    }
    if (!shippingAddress.country.trim()) {
      setError(t('validation.countryRequired'));
      return false;
    }
    setError(null);
    return true;
  };

  const handleNextStep = () => {
    if (step === 'info') {
      if (validateCustomerInfo()) {
        setStep('shipping');
      }
    } else if (step === 'shipping') {
      if (validateShippingAddress()) {
        setStep('review');
      }
    }
  };

  const handleSubmitOrder = async () => {
    if (!store) return;

    try {
      setSubmitting(true);
      setError(null);

      const orderData = {
        customer: customerInfo,
        shippingAddress,
        items: cartItems.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          productName: item.productName,
          variantName: item.variantName,
          price: item.price,
          quantity: item.quantity,
        })),
      };

      const response = await fetch(`/api/public/stores/${storeSlug}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error || 'Failed to place order');
      }

      const result = await response.json() as { order: { id: string; order_number: string } };

      // Clear the cart
      localStorage.removeItem(`cart_${store.id}`);

      // Navigate to confirmation page
      navigate(`/s/${storeSlug}/order/${result.order.order_number}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error && !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('storefront.storeNotFound')}</h1>
          <p className="text-gray-500 mb-6">{t('storefront.storeNotFoundDescription')}</p>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
            {t('storefront.goToHomepage')}
          </Link>
        </div>
      </div>
    );
  }

  if (!store) return null;

  return (
    <div className="min-h-screen bg-gray-50" dir={storeDirection} lang={storeLocale}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  {store.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-xl font-bold">{store.name}</span>
            </Link>

            <Link
              to={`/s/${store.slug}/cart`}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              {t('checkout.backToCart')}
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setStep('info')}
              className={`flex items-center gap-2 ${step === 'info' ? 'text-teal-600 font-medium' : 'text-gray-500'}`}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                step === 'info' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>1</span>
              <span className="hidden sm:inline">{t('checkout.information')}</span>
            </button>
            <div className="flex-1 h-px bg-gray-200 mx-4"></div>
            <button
              onClick={() => step !== 'info' && setStep('shipping')}
              disabled={step === 'info'}
              className={`flex items-center gap-2 ${step === 'shipping' ? 'text-teal-600 font-medium' : 'text-gray-500'} ${step === 'info' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                step === 'shipping' ? 'bg-teal-600 text-white' : step === 'review' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>2</span>
              <span className="hidden sm:inline">{t('checkout.shipping')}</span>
            </button>
            <div className="flex-1 h-px bg-gray-200 mx-4"></div>
            <button
              onClick={() => step === 'review' && setStep('review')}
              disabled={step !== 'review'}
              className={`flex items-center gap-2 ${step === 'review' ? 'text-teal-600 font-medium' : 'text-gray-500'} ${step !== 'review' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                step === 'review' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>3</span>
              <span className="hidden sm:inline">{t('checkout.review')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            {/* Customer Information Step */}
            {step === 'info' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('checkout.contactInformation')}</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.email')} *
                    </label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder={t('checkout.emailPlaceholder')}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.firstName')} *
                      </label>
                      <input
                        type="text"
                        value={customerInfo.firstName}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.lastName')} *
                      </label>
                      <input
                        type="text"
                        value={customerInfo.lastName}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.phone')}
                    </label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder={t('checkout.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={handleNextStep}
                    className="px-6 py-3 rounded-lg text-white font-medium"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {t('checkout.continueToShipping')}
                  </button>
                </div>
              </div>
            )}

            {/* Shipping Address Step */}
            {step === 'shipping' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('checkout.shippingAddress')}</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.addressLine1')} *
                    </label>
                    <input
                      type="text"
                      value={shippingAddress.address1}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address1: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.addressLine2')}
                    </label>
                    <input
                      type="text"
                      value={shippingAddress.address2}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address2: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.city')} *
                      </label>
                      <input
                        type="text"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.state')}
                      </label>
                      <input
                        type="text"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.postalCode')} *
                      </label>
                      <input
                        type="text"
                        value={shippingAddress.postalCode}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.country')} *
                      </label>
                      <select
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="IL">Israel</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-between">
                  <button
                    onClick={() => setStep('info')}
                    className="px-6 py-3 text-gray-600 font-medium hover:text-gray-900"
                  >
                    {t('checkout.backToInformation')}
                  </button>
                  <button
                    onClick={handleNextStep}
                    className="px-6 py-3 rounded-lg text-white font-medium"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {t('checkout.reviewOrder')}
                  </button>
                </div>
              </div>
            )}

            {/* Review Step */}
            {step === 'review' && (
              <div className="space-y-6">
                {/* Customer Info Summary */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t('checkout.contactInformation')}</h2>
                    <button
                      onClick={() => setStep('info')}
                      className="text-sm text-teal-600 hover:text-teal-700"
                    >
                      {t('checkout.edit', 'Edit')}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>{customerInfo.firstName} {customerInfo.lastName}</p>
                    <p>{customerInfo.email}</p>
                    {customerInfo.phone && <p>{customerInfo.phone}</p>}
                  </div>
                </div>

                {/* Shipping Summary */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t('checkout.shippingAddress')}</h2>
                    <button
                      onClick={() => setStep('shipping')}
                      className="text-sm text-teal-600 hover:text-teal-700"
                    >
                      {t('checkout.edit', 'Edit')}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>{shippingAddress.address1}</p>
                    {shippingAddress.address2 && <p>{shippingAddress.address2}</p>}
                    <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}</p>
                    <p>{shippingAddress.country}</p>
                  </div>
                </div>

                {/* Items Summary */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('order.items')}</h2>
                  <div className="space-y-3">
                    {cartItems.map((item) => (
                      <div key={`${item.productId}-${item.variantId}`} className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-gray-900">{item.productName}</span>
                          {item.variantName && <span className="text-gray-500"> - {item.variantName}</span>}
                          <span className="text-gray-400"> x {item.quantity}</span>
                        </div>
                        <span className="font-medium">{formatPrice(item.price * item.quantity, store.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">{t('checkout.payOnDelivery')}</p>
                      <p className="mt-1">{t('checkout.payOnDeliveryDescription')}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep('shipping')}
                    className="px-6 py-3 text-gray-600 font-medium hover:text-gray-900"
                  >
                    {t('checkout.backToShipping')}
                  </button>
                  <button
                    onClick={handleSubmitOrder}
                    disabled={submitting}
                    className="px-8 py-3 rounded-lg text-white font-medium disabled:opacity-50"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {submitting ? t('checkout.placingOrder') : t('checkout.placeOrder')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('checkout.orderSummary')}</h2>

              {/* Items */}
              <div className="space-y-3 pb-4 border-b border-gray-200">
                {cartItems.map((item) => (
                  <div key={`${item.productId}-${item.variantId}`} className="flex gap-3">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden relative">
                      {item.image ? (
                        <img src={item.image} alt={item.productName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-gray-500 text-white text-xs rounded-full flex items-center justify-center">
                        {item.quantity}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.productName}</p>
                      {item.variantName && (
                        <p className="text-xs text-gray-500">{item.variantName}</p>
                      )}
                      <p className="text-sm font-medium mt-1" style={{ color: theme.primaryColor }}>
                        {formatPrice(item.price, store.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 py-4 border-b border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('cart.subtotal')}</span>
                  <span className="font-medium">{formatPrice(subtotal, store.currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('cart.shipping')}</span>
                  <span className="text-gray-500">{t('order.free')}</span>
                </div>
              </div>

              <div className="flex justify-between py-4 text-lg font-semibold">
                <span>{t('cart.total')}</span>
                <span style={{ color: theme.primaryColor }}>
                  {formatPrice(subtotal, store.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <StoreFooter store={store} direction={storeDirection} />

      {/* Cookie Consent */}
      <CookieConsent
        storeId={store.id}
        storeSlug={store.slug}
        primaryColor={theme.primaryColor}
      />
    </div>
  );
}
